import { createFileRoute } from "@tanstack/react-router"

import { pageHead } from "@/lib/metadata"
import { getHome } from "@/server/web"

import Link from "@/components/link"
import { Container } from "@/components/layout/container"
import { SiteShell } from "@/components/layout/site-shell"
import { TwoToneHeading } from "@/components/layout/two-tone-heading"
import { Betas, GetStarted, Latest } from "@/components/home/home-blocks"
import { LaunchListButton } from "@/components/home/launch-list-button"
import { Mosaic } from "@/components/home/mosaic"
import { Button } from "@anysola/ui/button"
import { siteConfig } from "@/lib/site"

/**
 * Konsol: a short heading, then the apps themselves (a mosaic of their real
 * interfaces with sample values), how to start, betas and the latest posts.
 * No block repeats the copy-beside-a-card pattern.
 */
function Page() {
  const { betas, news, changelog } = Route.useLoaderData()
  return (
    <SiteShell>
      <Container className="pt-10 pb-16 sm:pt-16 sm:pb-24">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_24rem] lg:items-end lg:gap-12">
          <TwoToneHeading
            as="h1"
            lead="Everyday tasks,"
            rest="handled by AI."
            stacked
            className="text-[2.5rem] leading-[1.04] tracking-[-0.045em] sm:text-6xl"
          />
          <div className="lg:pb-1.5">
            <p className="text-base leading-relaxed text-muted-foreground">
              Apps for the small jobs that eat your day, on one account and one
              balance.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-2">
              <LaunchListButton />
              <Button
                size="xl"
                variant="ghost"
                render={<Link href="/pricing" />}
              >
                Pricing
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-10 sm:mt-14">
          <Mosaic />
        </div>

        <div className="mt-16 space-y-16 sm:mt-24 sm:space-y-20">
          <GetStarted />
          <Betas programs={betas} />
          <Latest news={news} changelog={changelog} />
        </div>
      </Container>
    </SiteShell>
  )
}

export const Route = createFileRoute("/")({
  // The products, news and changelog sections read published rows; the
  // service keeps them 300 s (the Next page's `revalidate`), a few minutes of
  // staleness being fine for a marketing page.
  loader: () => getHome(),
  head: () =>
    // The root template skips its own segment's page, so the tab title is
    // spelled out; the preview keeps the bare "Home" from the helper.
    pageHead({
      title: siteConfig.shareTitle,
      description: siteConfig.description,
      path: "/",
      image: "/opengraph-image",
      absoluteTitle: siteConfig.title,
    }),
  component: Page,
})
